import React, { Component } from "react";
import { Button, Tooltip } from "antd";
import { LeftOutlined, RightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { SegmentedItem } from "../../../../Components/Items";
import "../cmms.css";

// Calendario del CMMS (agenda, calendarizacion del contrato y viajes). Eventos de todo el dia;
// los que traen `arrastrable` se pueden mover a otro dia (onMover). No usa CalendarItem
// porque ese abre el detalle del CRM al dar clic en un evento.
//
// events: [{ id, title, subtitulo, start, end, color, arrastrable, ...lo que se quiera }]
// onRango({ desde, hasta, fecha, vista }) se llama al montar y cada vez que cambia lo visible.
// Opcionales: festivos [{ fecha, nombre }] (el dia se sombrea y muestra el nombre), textoNuevo
// (al pasar el mouse por un dia, ej. "+ Viaje"; solo con onDiaClick), extra (debajo de la barra),
// ayuda (texto del icono de informacion junto a la leyenda), nombreEvento (columna de la Lista;
// "Servicio" si no se da).

moment.locale("es");
const localizer = momentLocalizer(moment);
const CalendarioDnD = withDragAndDrop(Calendar);

const VISTAS = [
    { label: "Mes", value: Views.MONTH },
    { label: "Semana", value: Views.WEEK },
    { label: "Lista", value: Views.AGENDA },
];

const MENSAJES = {
    date: "Fecha",
    time: "Hora",
    event: "Servicio",
    allDay: "Todo el día",
    noEventsInRange: "No hay nada en este periodo.",
    showMore: (total) => `+${total} más`,
};

const claveDia = (f) => moment(f).format("YYYY-MM-DD");
const conMayuscula = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const rangoVisible = (fecha, vista) => {
    const f = moment(fecha);
    if (vista === Views.WEEK) return { desde: f.clone().startOf("week"), hasta: f.clone().endOf("week") };
    if (vista === Views.AGENDA) return { desde: f.clone().startOf("day"), hasta: f.clone().add(30, "days").endOf("day") };
    return { desde: f.clone().startOf("month").startOf("week"), hasta: f.clone().endOf("month").endOf("week") };
};

const Evento = ({ event }) => (
    <div className="cmms-cal-evento" style={{ borderLeftColor: event.color || "#9aa7b4" }}>
        <div className="cmms-cal-evento-titulo">{event.title}</div>
        {event.subtitulo ? <div className="cmms-cal-evento-sub">{event.subtitulo}</div> : null}
    </div>
);

class CalendarioCMMS extends Component {
    state = { fecha: this.props.fechaInicial ? moment(this.props.fechaInicial).toDate() : new Date(), vista: this.props.vistaInicial || Views.MONTH };

    componentDidMount() {
        this.avisarRango(this.state.fecha, this.state.vista);
    }

    avisarRango = (fecha, vista) => {
        const r = rangoVisible(fecha, vista);
        this.props.onRango?.({ ...r, fecha: moment(fecha), vista });
    };

    navegar = (accion) => {
        const unidad = this.state.vista === Views.WEEK ? "week" : "month";
        const f = accion === "hoy" ? moment() : moment(this.state.fecha).add(accion === "sig" ? 1 : -1, unidad);
        this.setState({ fecha: f.toDate() }, () => this.avisarRango(this.state.fecha, this.state.vista));
    };

    titulo = () => {
        const f = moment(this.state.fecha);
        if (this.state.vista === Views.WEEK) {
            const r = rangoVisible(f, Views.WEEK);
            return `${r.desde.format("D MMM")} – ${r.hasta.format("D MMM YYYY")}`;
        }
        if (this.state.vista === Views.AGENDA) return `Del ${f.format("D MMM")} al ${f.clone().add(30, "days").format("D MMM YYYY")}`;
        return conMayuscula(f.format("MMMM YYYY"));
    };

    // { "AAAA-MM-DD": nombre } de los dias festivos.
    festivosPorDia = () => {
        const lista = this.props.festivos || [];
        if (this.ultimosFestivos !== lista) {
            this.ultimosFestivos = lista;
            this.mapaFestivos = new Map(lista.map((f) => [String(f.fecha).slice(0, 10), f.nombre || "Día festivo"]));
        }
        return this.mapaFestivos;
    };

    // Numero del dia (hoy en circulo) y, si es festivo, su nombre.
    EncabezadoDia = ({ date }) => {
        const festivo = this.festivosPorDia().get(claveDia(date));
        const hoy = moment(date).isSame(moment(), "day");
        return (
            <div className="cmms-cal-fecha">
                <span className={`cmms-cal-num${hoy ? " cmms-cal-num-hoy" : ""}`}>{moment(date).format("D")}</span>
                {festivo ? (
                    <span className="cmms-cal-festivo-nombre" title={festivo}>
                        {festivo}
                    </span>
                ) : null}
            </div>
        );
    };

    // Vista semana: "lun 12" y, debajo, el festivo.
    EncabezadoSemana = ({ date }) => {
        const festivo = this.festivosPorDia().get(claveDia(date));
        const hoy = moment(date).isSame(moment(), "day");
        return (
            <div className="cmms-cal-semana-dia">
                <span className="cmms-cal-semana-nombre">{moment(date).format("ddd")}</span>
                <span className={`cmms-cal-num${hoy ? " cmms-cal-num-hoy" : ""}`}>{moment(date).format("D")}</span>
                {festivo ? <span className="cmms-cal-festivo-nombre">{festivo}</span> : null}
            </div>
        );
    };

    propsDia = (date) => {
        const m = moment(date);
        const clases = [];
        if (m.day() === 0 || m.day() === 6) clases.push("cmms-cal-dia-finde");
        if (this.festivosPorDia().has(claveDia(date))) clases.push("cmms-cal-dia-festivo");
        return { className: clases.join(" ") };
    };

    render() {
        const { events = [], onSeleccionar, onMover, onDiaClick, alto = 720, leyenda, extra, ayuda, textoNuevo, nombreEvento = "Servicio" } = this.props;
        const conNuevo = !!onDiaClick && !!textoNuevo;
        return (
            <div
                className={`cmms-calendario${conNuevo ? " cmms-cal-con-nuevo" : ""}`}
                style={conNuevo ? { "--cmms-cal-nuevo": JSON.stringify(textoNuevo) } : undefined}
            >
                <div className="cmms-cal-barra">
                    <div className="cmms-cal-navegacion">
                        <Button.Group>
                            <Button icon={<LeftOutlined />} onClick={() => this.navegar("ant")} />
                            <Button onClick={() => this.navegar("hoy")}>Hoy</Button>
                            <Button icon={<RightOutlined />} onClick={() => this.navegar("sig")} />
                        </Button.Group>
                        <span className="cmms-cal-titulo">{this.titulo()}</span>
                    </div>
                    {leyenda || ayuda ? (
                        <div className="cmms-cal-leyenda">
                            {leyenda}
                            {ayuda ? (
                                <Tooltip title={ayuda}>
                                    <InfoCircleOutlined className="cmms-cal-ayuda" />
                                </Tooltip>
                            ) : null}
                        </div>
                    ) : null}
                    <SegmentedItem
                        options={VISTAS}
                        value={this.state.vista}
                        onChange={(vista) => this.setState({ vista }, () => this.avisarRango(this.state.fecha, vista))}
                    />
                </div>
                {extra ? <div className="cmms-cal-extra">{extra}</div> : null}
                <div style={{ height: alto }}>
                    <CalendarioDnD
                        localizer={localizer}
                        culture="es"
                        events={events}
                        date={this.state.fecha}
                        view={this.state.vista}
                        views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
                        onNavigate={(fecha) => this.setState({ fecha }, () => this.avisarRango(fecha, this.state.vista))}
                        onView={(vista) => this.setState({ vista }, () => this.avisarRango(this.state.fecha, vista))}
                        toolbar={false}
                        popup
                        selectable={!!onDiaClick}
                        onSelectSlot={(slot) => onDiaClick?.(moment(slot.start))}
                        messages={{ ...MENSAJES, event: nombreEvento }}
                        startAccessor="start"
                        endAccessor="end"
                        allDayAccessor={() => true}
                        showAllEvents={false}
                        resizable={false}
                        draggableAccessor={(e) => !!e.arrastrable && !!onMover}
                        onEventDrop={({ event, start }) => {
                            if (!moment(start).isSame(event.start, "day")) onMover?.(event, moment(start));
                        }}
                        onSelectEvent={(e) => onSeleccionar?.(e)}
                        tooltipAccessor={(e) => [e.title, e.subtitulo, e.detalle].filter(Boolean).join("\n")}
                        eventPropGetter={(e) => ({ className: "cmms-cal-rbc-evento", style: { backgroundColor: `${e.color || "#9aa7b4"}24`, color: "#1f2a37" } })}
                        dayPropGetter={this.propsDia}
                        components={{ event: Evento, month: { dateHeader: this.EncabezadoDia }, week: { header: this.EncabezadoSemana } }}
                        formats={{
                            weekdayFormat: (d) => moment(d).format("ddd"),
                            dayFormat: (d) => moment(d).format("ddd D"),
                            agendaDateFormat: (d) => moment(d).format("ddd D MMM"),
                        }}
                    />
                </div>
            </div>
        );
    }
}

// Leyenda de colores (chips). rayas = se pinta como el dia festivo (fondo rayado).
export const LeyendaCMMS = ({ items }) => (
    <>
        {items.map((i) => (
            <Tooltip key={i.label} title={i.ayuda || ""}>
                <span className="cmms-cal-chip">
                    <span className={`cmms-cal-chip-punto${i.rayas ? " cmms-cal-chip-rayas" : ""}`} style={i.rayas ? undefined : { background: i.hex }} />
                    {i.label}
                </span>
            </Tooltip>
        ))}
    </>
);

export default CalendarioCMMS;
